import { useEffect, useState } from "react";
import { ColumnsType } from "antd/es/table";
import Datatable from "../../core/common/dataTable";
import { PageBreadcrumb } from "../../core/common/BreadCrumbForm";
import { all_routes } from "../router/all_routes";
import { FileAddOutlined, UserAddOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import Footer from "../../core/common/Footer";
import { cellWidth } from "../../core/methods/tableCellWidth";
import useApi from "../../core/customhook/useApi";
import webservice_url from "../../core/webservice/Webservice_url";
import Loading from "../../core/common/custom-loading/Loading";
import { PaginationType } from "../../types/common-type";
import { formattedDate } from "../../core/methods/formattedDate";
import {
  safeDateSort,
  safeNumberSort,
  safeStringSort,
} from "../../core/methods/sorterMethods";
import { exportAntdTableToExcel } from "../utils/exportExcel";
import dayjs from "dayjs";

export interface OrderTypes {
  order_id: string;
  order_type: "bulk";
  order_status: "pending" | "completed" | "failed" | "processing" | string;
  amount: number;
  total_amount: number;
  valid_cases: number;
  invalid_cases: number;
  created_on: string;
  created_by_id: string;
  full_name: string;
  created_by_email: string;
}

const breadcrumbItem = [
  // { label: "Manage Order", path: all_routes.trackCase },
  { label: "Create Order" },
  { label: "Manage Order" },
];

const ManageOrder = () => {
  const navigate = useNavigate();
  const [dataList, setDataList] = useState<OrderTypes[]>([]);
  const [expandedDescriptions, setExpandedDescriptions] = useState<string[]>(
    [],
  );
  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000,
    total: 1,
  });

  // API
  const { callApi, loading, error } = useApi<any>(webservice_url.getAllOrders, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const fetchData = async () => {
    try {
      const res = await callApi({
        page: pagination?.current ?? 1,
        limit: pagination?.pageSize ?? 10,
        order_status: "", //pending
        order_type: "", // single , bulk
      });

      const data = res?.data || null;
      if (!data) return;
      setDataList(Array.isArray(data?.orders) ? data?.orders : []);
      setPagination((prev) => ({
        ...prev,
        total: data?.pagination?.total || 0,
      }));
    } catch (err: any) {
      console.error(err);
      setDataList([]);
    }
  };
  useEffect(() => {
    fetchData();
  }, [pagination?.current, pagination?.pageSize]);

  //   handle table change
  const handleTableChange = (pgConfig: any) => {
    if (
      pgConfig.current !== pagination.current ||
      pgConfig.pageSize !== pagination.pageSize
    ) {
      const pageSizeChanged = pgConfig.pageSize !== pagination.pageSize;
      setPagination((prev) => ({
        ...prev,
        current: pageSizeChanged ? 1 : pgConfig.current,
        pageSize: pgConfig.pageSize,
      }));
    }
  };

  const formatDisplayValue = (value?: string) => {
    if (!value) return "-";
    return value
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      width: 80,
      align: "center",
      render: (_: any, __: any, index: number) =>
        ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 10) +
        index +
        1,
      excelFormatter: (_: any, __: any, index: number) =>
        ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 10) +
        index +
        1,
    } as any,
    {
      title: "Order ID",
      dataIndex: "order_id",
      key: "order_id",
      onCell: () => cellWidth(300),
      sorter: (a, b) => safeStringSort(a.order_id, b.order_id),
    },

    {
      title: "Package Description",
      dataIndex: "sop",
      key: "sop",
      onCell: () => cellWidth(350),
      sorter: (a, b) => safeStringSort(a.sop, b.sop),
      render: (text: string, record: any) => {
        const description = text || "-";
        const orderId = record?.order_id;
        const isExpanded = expandedDescriptions.includes(orderId);
        const isLong = description.length > 45;

        return (
          <div>
            <div
              style={{
                maxHeight: isExpanded ? "1000px" : "63px",
                overflow: "hidden",
                transition: "max-height 0.5s ease",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                lineHeight: "21px",
              }}
            >
              {isExpanded || !isLong
                ? description
                : `${description.slice(0, 45)}...`}
            </div>

            {isLong && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 mt-1"
                style={{ textDecoration: "none" }}
                onClick={() => {
                  setExpandedDescriptions((prev) =>
                    isExpanded
                      ? prev.filter((id) => id !== orderId)
                      : [...prev, orderId],
                  );
                }}
              >
                {isExpanded ? "Show Less" : "Learn More"}
              </button>
            )}
          </div>
        );
      },
    },
    {
      title: "Package",
      dataIndex: "package_name",
      key: "package_name",
      onCell: () => cellWidth(300),
      sorter: (a, b) => safeStringSort(a.package_name, b.package_name),
      render: (text: string) => formatDisplayValue(text),
    },
    {
      title: "Created On",
      dataIndex: "created_on",
      key: "created_on",
      onCell: () => cellWidth(120),
      render: (text: string) => {
        const date = text ? dayjs(text).format("DD-MM-YYYY") : "";
        return <>{date}</>;
      },
      sorter: (a: any, b: any) => safeDateSort(a.created_on, b.created_on),
      excelFormatter: (text: string) => formattedDate(text) || 0,
    } as any,
    {
      title: "Valid",
      dataIndex: "valid_cases",
      key: "valid_cases",
      align: "center",
      sorter: (a, b) => safeNumberSort(a.valid_cases, b.valid_cases),
    },
    {
      title: "Invalid",
      dataIndex: "invalid_cases",
      key: "invalid_cases",
      align: "center",
      sorter: (a, b) => safeNumberSort(a.invalid_cases, b.invalid_cases),
    },

    // {
    //   title: "Total Amount",
    //   dataIndex: "total_amount",
    //   key: "total_amount",
    //   render: (text: string) => `₹${text?.toLocaleString() ?? 0}`,
    //   sorter: (a: any, b: any) => safeNumberSort(a.total_amount, b.total_amount),
    //   excelFormatter: (text: string) => Number(text) || 0,
    // } as any,
    {
      title: "Action",
      dataIndex: "",
      onCell: () => cellWidth(100),
      render: (text: string, record: any) => {
        return (
          <button
            type="button"
            className="btn btn-sm btn-info"
            onClick={() => {
              localStorage.setItem("create-order-tab", "singleInvite");
              navigate(`${all_routes.assingOrderInvite}/${record?.order_id}`);
            }}
          >
             <FileAddOutlined />
            &nbsp;Invite
          </button>
        );
      },
      excludeFromExport: true,
    },
  ];

  // handleDownloadExcel
  const handleDownloadExcel = () => {
    exportAntdTableToExcel({
      columns,
      dataSource: dataList,
      fileName: "Assign_Order_Report",
      sheetName: "Assign Order",
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* PageBreadcrumb */}
        <PageBreadcrumb title="Manage Orders" breadcrumbs={breadcrumbItem} />
        {/* /PageBreadcrumb */}

        {/* Download Excel Button */}

        {loading ? (
          <Loading />
        ) : (
          <>
            <div className="card mt-3">
              <div className="text-end px-2 py-2 pb-0">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleDownloadExcel}
                  disabled={loading}
                >
                  <i className="bi bi-download pe-2"></i>
                  Export to Excel
                </button>
              </div>
              <Datatable
                columns={columns as ColumnsType<any>}
                dataSource={dataList}
                fixedHeader
                tableHeight={650}
                rowKey="order_id"
                Selection={false}
                pagination={pagination}
                handleTableChange={handleTableChange}
              />
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ManageOrder;
